import React from 'react';
import { StanBadge } from '@/components';
import { cn, createIconElement } from '@/lib';
import { getTimelineGridTemplate } from '../../constants/gridColumns';
import { getEmailDelayText } from '../../emails/logic';
import type { EmailRowProps } from './FlowEmailTimeline.types';

import EmailIcon from '@/assets/icons/ic-email-flows.svg';
import ClockIcon from '@/assets/icons/ic-clock.svg';
import { formatEmailSubjectPlainText } from '../../logic/templateVariables.logic';

/**
 * EmailRow Component
 * Displays a single email row in the timeline
 */
export const EmailRow = React.memo<EmailRowProps>(
    ({ email, flowStatus, analytics, onEmailClick, compact = false, isActive: isActiveOverride }) => {
        const ClockIconSvg = createIconElement(ClockIcon);
        const isActive = isActiveOverride ?? false;
        const formattedSubject = formatEmailSubjectPlainText(email.data?.subject || 'Untitled Email');

        const EmailIconSvg = createIconElement(EmailIcon, {
            className: cn('flex-shrink-0 opacity-100 size-4', {
                'opacity-100 ': isActive,
            }),
        });
        const isPublished = email.status === 'published' && flowStatus === 'published';
        const isNewEmail = email.flow_email_id === 0;

        // Compact mode (for navigation card)
        if (compact) {
            return (
                <div
                    role='button'
                    className={cn(
                        'relative z-10 flex items-start gap-3 rounded-lg py-2.5 transition duration-300',
                        !isActive && 'cursor-pointer hover:-translate-y-px focus-visible:-translate-y-px'
                    )}
                    onClick={() => !isActive && onEmailClick(email)}
                >
                    <div
                        className={cn(
                            'bg-neutral-subtle flex size-6.5 items-center justify-center rounded-full transition-all',
                            isActive && 'bg-brand-subtle size-7.5 -translate-x-0.5'
                        )}
                    >
                        {EmailIconSvg}
                    </div>

                    <div className='flex min-w-0 flex-1 pt-1.5'>
                        <p
                            className={cn(
                                'text-label-sm-regular! line-clamp-2 flex-1 leading-5 transition-colors duration-300',
                                isActive ? 'text-dark pt-px font-bold' : 'text-gray'
                            )}
                            dangerouslySetInnerHTML={{ __html: formattedSubject }}
                        />
                    </div>

                    {!isNewEmail && (
                        <div className='flex flex-nowrap'>
                            {email.status === 'published' && (
                                <StanBadge variant='primary' size='sm' icon={ClockIconSvg}>
                                    {getEmailDelayText(email)}
                                </StanBadge>
                            )}

                            {email.status === 'draft' && (
                                <StanBadge variant='muted' size='sm'>
                                    Draft
                                </StanBadge>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                role='button'
                className='hover:bg-neutral-dimmed group focus-visible:bg-neutral-dimmed relative z-10 grid cursor-pointer items-center rounded-lg transition-colors duration-200 max-md:py-2.5 md:gap-4 md:p-2.5'
                style={{
                    gridTemplateColumns: '1fr auto',
                }}
                onClick={() => onEmailClick(email)}
            >
                <div
                    className='text-body-sm grid items-center max-md:block'
                    style={{
                        gridTemplateColumns: getTimelineGridTemplate(),
                    }}
                >
                    <div className='text-body-sm flex w-full! items-center truncate overflow-hidden pr-2 font-medium'>
                        <div className='bg-neutral-subtle mr-3 flex size-6.5 items-center justify-center rounded-full transition-colors'>
                            {EmailIconSvg}
                        </div>
                        <span
                            dangerouslySetInnerHTML={{ __html: formattedSubject }}
                            className='w-0! flex-1! truncate! md:truncate'
                        />
                    </div>

                    <div className='max-md:hidden'>
                        <span>{analytics.sales}</span>
                    </div>

                    <div className='max-md:hidden'>{analytics.sent}</div>

                    <div className='max-md:hidden'>{analytics.openRate}</div>

                    <div className='max-md:hidden'>{analytics.clickRate}</div>

                    <div className='hidden md:flex'>
                        <StanBadge variant={isPublished ? 'success' : 'muted'} size='sm'>
                            {isPublished ? 'Active' : 'Draft'}
                        </StanBadge>
                    </div>
                </div>

                {/* Actions space - matches StanDataRow .header-actions-space */}
                <div className='w-8 max-md:hidden' />
            </div>
        );
    }
);

EmailRow.displayName = 'EmailRow';
